import React, { useEffect, useState } from 'react';
import { Check, ChevronDown, Pencil, Plus, X } from 'lucide-react';
import { useIntl } from 'react-intl';
import { Todo, OnComplete, RecurrenceRule } from '../../types/todo';
import { useTodos } from '../../hooks/useTodos';
import TodoModal from './TodoModal';
import Modal from '../common/Modal';
import SectionHeader from '../common/SectionHeader';
import Spinner from '../common/Spinner';
import Collapsible from '../common/Collapsible';
import { useToast } from '../common/Toast';
import { toLocalDateStr } from '../../utils/dateUtils';
import { TASK_TITLE_MAX } from '../common/HabitModals/HabitFormFields';
import './TodoList.scss';

interface TodoListProps {
  selectedDate: Date;
  createWithTime?: string | null;
  onCreateWithTimeHandled?: () => void;
}

const TodoList: React.FC<TodoListProps> = ({ selectedDate, createWithTime, onCreateWithTimeHandled }) => {
  const { formatMessage: t } = useIntl();
  const dateStr = toLocalDateStr(selectedDate);
  const { todos, allTodos, isLoading, addTodo, updateTodo, toggleComplete, removeTodo, removeSeries } = useTodos(dateStr);
  const { showToast } = useToast();
  const [modalTodo, setModalTodo] = useState<Todo | null | undefined>(undefined);
  const [modalTime, setModalTime] = useState('');
  const [confirmDeleteTodo, setConfirmDeleteTodo] = useState<Todo | null>(null);
  const [quickTitle, setQuickTitle] = useState('');
  const doneTodos = todos.filter((item) => item.completed);
  const activeTodos = todos.filter((item) => !item.completed);
  const allTasksDone = todos.length > 0 && doneTodos.length === todos.length;
  const [tasksCollapsed, setTasksCollapsed] = useState(allTasksDone);
  const [doneCollapsed, setDoneCollapsed] = useState(true);

  useEffect(() => {
    if (allTasksDone) setTasksCollapsed(true);
  }, [allTasksDone]);

  useEffect(() => {
    if (!createWithTime) return;
    setModalTodo(null);
    setModalTime(createWithTime);
    onCreateWithTimeHandled?.();
  }, [createWithTime, onCreateWithTimeHandled]);

  const handleEdit = (todo: Todo) => {
    setModalTime('');
    setModalTodo(todo);
  };

  const handleDeleteClick = (todo: Todo) => {
    if (todo.recurrenceRule || todo.recurrenceParentId) {
      setConfirmDeleteTodo(todo);
    } else {
      removeTodo(todo.id);
    }
  };

  const handleSave = (data: {
    title: string;
    scheduledTime: string | null;
    recurrenceRule: RecurrenceRule | null;
    onComplete: OnComplete;
  }) =>
    addTodo({
      title: data.title,
      scheduledDate: dateStr,
      scheduledTime: data.scheduledTime,
      recurrenceRule: data.recurrenceRule,
      onComplete: data.onComplete,
    }).then(
      () => showToast(t({ id: 'toast.task.added' })),
      () => showToast(t({ id: 'toast.task.saveFailed' }), 'error'),
    );

  const handleQuickAdd = (e: React.FormEvent) => {
    e.preventDefault();
    const title = quickTitle.trim();
    if (!title) return;
    setQuickTitle('');
    void addTodo({
      title,
      scheduledDate: dateStr,
      scheduledTime: null,
      recurrenceRule: null,
      onComplete: 'archive',
    }).then(
      () => showToast(t({ id: 'toast.task.added' })),
      () => showToast(t({ id: 'toast.task.saveFailed' }), 'error'),
    );
  };

  const getRecurrenceLabel = (todo: Todo): string | null => {
    const r = todo.recurrenceRule
      ?? (todo.recurrenceParentId
        ? allTodos.find((item) => item.id === todo.recurrenceParentId)?.recurrenceRule
        : null);
    if (!r) return null;
    switch (r.type) {
      case 'daily': return t({ id: 'daily.rec.daily' });
      case 'weekly': return t({ id: 'daily.rec.weekly' });
      case 'biweekly': return t({ id: 'daily.rec.biweekly' });
      case 'monthly': return t({ id: 'daily.rec.monthly' });
      case 'interval':
        return r.unit === 'week'
          ? t({ id: 'daily.rec.intervalWeeks' }, { every: r.every ?? 2 })
          : t({ id: 'daily.rec.intervalDays' }, { every: r.every ?? 2 });
      default: return null;
    }
  };

  return (
    <div className={`todo-list${tasksCollapsed && allTasksDone ? ' todo-list--complete' : ''}`}>
      {tasksCollapsed && allTasksDone ? (
        <button
          type="button"
          className="daily-complete-banner"
          onClick={() => setTasksCollapsed(false)}
          aria-expanded={false}
        >
          <Check className="daily-complete-banner__icon" size={16} strokeWidth={2.5} />
          <span className="daily-complete-banner__label">{t({ id: 'daily.tasks.allDone' })}</span>
          <ChevronDown className="daily-complete-banner__chevron" size={16} />
        </button>
      ) : (
        <>
      <SectionHeader
        title={t({ id: 'daily.tasks.title' })}
        count={
          todos.length > 0
            ? t({ id: 'daily.tasks.count' }, { completed: doneTodos.length, total: todos.length })
            : undefined
        }
        collapsed={tasksCollapsed}
        onToggle={todos.length > 0 ? () => setTasksCollapsed((c) => !c) : undefined}
        toggleLabel={tasksCollapsed ? t({ id: 'daily.tasks.expand' }) : t({ id: 'daily.tasks.collapse' })}
      />

      {isLoading ? (
        <Spinner block />
      ) : tasksCollapsed ? null : (
        <>
      {todos.length === 0 && allTodos.length === 0 && (
        <div className="empty-copy">
          <p className="empty-copy__body">{t({ id: 'daily.tasks.empty' })}</p>
          <p className="empty-copy__example">{t({ id: 'daily.tasks.emptyExample' })}</p>
        </div>
      )}

      <ul className="todo-list__items">
        {activeTodos.map(todo => (
          <li key={todo.id} className="todo-item">
            <button
              className="todo-item__check"
              onClick={() => toggleComplete(todo.id)}
              type="button"
            >
              <span className="todo-item__box" />
            </button>

            <span className="todo-item__title">{todo.title}</span>

            <div className="todo-item__meta">
              {getRecurrenceLabel(todo) && (
                <span className="todo-item__badge todo-item__badge--rec" title={t({ id: 'daily.tasks.recurringBadge' })}>
                  ↻ {getRecurrenceLabel(todo)}
                </span>
              )}
              {todo.scheduledTime && (
                <span className="todo-item__time">{todo.scheduledTime}</span>
              )}
            </div>

            <button
              className="todo-item__edit"
              onClick={() => handleEdit(todo)}
              type="button"
              title={t({ id: 'daily.tasks.editTitle' })}
            ><Pencil size={13} /></button>
            <button
              className="todo-item__delete"
              onClick={() => handleDeleteClick(todo)}
              type="button"
              title={t({ id: 'daily.tasks.deleteTitle' })}
            ><X size={13} /></button>
          </li>
        ))}
      </ul>

      <form className="todo-quick-add" onSubmit={handleQuickAdd}>
        <button type="submit" className="todo-quick-add__btn" aria-label={t({ id: 'daily.tasks.add' })}>
          <Plus size={16} />
        </button>
        <input
          className="todo-quick-add__input"
          type="text"
          value={quickTitle}
          maxLength={TASK_TITLE_MAX}
          onChange={(e) => setQuickTitle(e.target.value.slice(0, TASK_TITLE_MAX))}
          placeholder={t({ id: 'daily.tasks.quickAdd' })}
          aria-label={t({ id: 'daily.tasks.quickAdd' })}
        />
      </form>

      {doneTodos.length > 0 && (
        <Collapsible
          title={t({ id: 'daily.tasks.completed' }, { count: doneTodos.length })}
          collapsed={doneCollapsed}
          onToggle={() => setDoneCollapsed(c => !c)}
          divider
          className="todo-list__done-section"
        >
          <ul className="todo-list__items">
            {doneTodos.map(todo => (
              <li key={todo.id} className="todo-item todo-item--done">
                <button
                  className="todo-item__check"
                  onClick={() => toggleComplete(todo.id)}
                  type="button"
                >
                  <span className="todo-item__box todo-item__box--checked">✓</span>
                </button>
                <span className="todo-item__title">{todo.title}</span>
                <div className="todo-item__meta" />
                <button
                  className="todo-item__delete"
                  onClick={() => handleDeleteClick(todo)}
                  type="button"
                  title={t({ id: 'daily.tasks.deleteTitle' })}
                >✕</button>
              </li>
            ))}
          </ul>
        </Collapsible>
      )}
        </>
      )}
        </>
      )}

      {modalTodo !== undefined && (
        <TodoModal
          todo={modalTodo}
          seriesRule={
            modalTodo
              ? modalTodo.recurrenceRule
                ?? allTodos.find((item) => item.id === modalTodo.recurrenceParentId)?.recurrenceRule
                ?? null
              : null
          }
          defaultDate={dateStr}
          defaultTime={modalTime}
          onSave={handleSave}
          onUpdate={updateTodo}
          onDelete={(id) => {
            const target = modalTodo?.id === id ? modalTodo : todos.find((item) => item.id === id);
            setModalTodo(undefined);
            setModalTime('');
            if (target) handleDeleteClick(target);
            else removeTodo(id);
          }}
          onDeleteSeries={removeSeries}
          onClose={() => {
            setModalTodo(undefined);
            setModalTime('');
          }}
        />
      )}

      {confirmDeleteTodo && (
        <Modal
          title={t({ id: 'daily.tasks.deleteRepeatingTitle' })}
          onClose={() => setConfirmDeleteTodo(null)}
          panelClassName="todo-confirm-modal"
          bodyClassName="todo-confirm-modal__body"
          maxWidth={560}
          footer={
            <div className="todo-confirm-modal__actions">
              <button
                type="button"
                className="todo-confirm-modal__btn todo-confirm-modal__btn--cancel"
                onClick={() => setConfirmDeleteTodo(null)}
              >
                {t({ id: 'common.cancel' })}
              </button>
              <button
                type="button"
                className="todo-confirm-modal__btn todo-confirm-modal__btn--occurrence"
                onClick={() => {
                  removeTodo(confirmDeleteTodo.id);
                  setConfirmDeleteTodo(null);
                }}
              >
                {t({ id: 'daily.tasks.deleteOccurrence' })}
              </button>
              <button
                type="button"
                className="todo-confirm-modal__btn todo-confirm-modal__btn--series"
                onClick={() => {
                  removeSeries(confirmDeleteTodo.recurrenceParentId ?? confirmDeleteTodo.id);
                  setConfirmDeleteTodo(null);
                }}
              >
                {t({ id: 'daily.tasks.deleteSeries' })}
              </button>
            </div>
          }
        >
          <p className="todo-confirm-modal__text">
            {t({ id: 'daily.tasks.deleteRepeatingBody' })}
          </p>
        </Modal>
      )}
    </div>
  );
};

export default TodoList;
